// client/src/components/tendermaker/BidAnalyzerPage.js
import React, { useEffect, useMemo, useState } from "react";
import { tmBidList, tmBidAnalyze, tmGetRun, tmListRuns } from "./tendermakerApi";
import ReviewWidget from "./ReviewWidget";
import Layout from "../Layout";


const box = {
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  padding: 12,
  background: "white",
};

const btn = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  background: "#fafafa",
  cursor: "pointer",
  fontWeight: 800,
};

const smallBtn = {
  ...btn,
  padding: "8px 10px",
  fontWeight: 700,
};

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function fmtTime(d) {
  try {
    if (!d) return "-";
    const dt =
      typeof d === "string"
        ? new Date(d)
        : d?.toDate?.()
        ? d.toDate()
        : new Date(d);
    if (Number.isNaN(dt.getTime())) return "-";
    return dt.toLocaleString();
  } catch {
    return "-";
  }
}

export default function BidAnalyzerPage() {
  const [bids, setBids] = useState([]);
  const [selectedBidId, setSelectedBidId] = useState("");
  const [mode, setMode] = useState("auto"); // auto|text|ocr
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [run, setRun] = useState(null);

  // ✅ history
  const [history, setHistory] = useState([]);
  const [histBusy, setHistBusy] = useState(false);
  const [histCursor, setHistCursor] = useState(null);
  const [histHasMore, setHistHasMore] = useState(false);
  const [histStatusFilter, setHistStatusFilter] = useState(""); // "", queued|running|done|error

  const selectedBid = useMemo(
    () => bids.find((b) => b.id === selectedBidId),
    [bids, selectedBidId]
  );

  async function refreshBids() {
    const r = await tmBidList();
    setBids(r.data || []);
  }

  async function loadHistory({
    reset = false,
    bidId = selectedBidId,
    statusFilter = histStatusFilter,
  } = {}) {
    if (!tmListRuns) return; // if you haven't added api function yet
    setHistBusy(true);
    try {
      const cursor = reset ? null : histCursor;
      const resp = await tmListRuns({
        bidId: bidId || undefined,
        status: statusFilter || undefined,
        limit: 20,
        cursor: cursor || undefined,
      });
      const items = resp?.data || [];
      const next = resp?.nextCursor || null;
      if (reset) {
        setHistory(items);
      } else {
        setHistory((prev) => [...prev, ...items]);
      }
      setHistCursor(next);
      setHistHasMore(Boolean(next));
    } catch (e) {
      // don't block analyzer if history fails
      console.error(e);
    } finally {
      setHistBusy(false);
    }
  }

  useEffect(() => {
    refreshBids().catch((e) => setError(String(e)));
  }, []);

  // When bid changes, reset run + status and load history for that bid
  useEffect(() => {
    setRun(null);
    setStatus("");
    setError("");
    setHistCursor(null);
    setHistHasMore(false);
    setHistory([]);
    if (selectedBidId) {
      loadHistory({ reset: true, bidId: selectedBidId }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBidId]);

  async function pollRun(runId) {
    for (;;) {
      const r = await tmGetRun(runId);
      setStatus(r.status || "");
      // optional progress if you write it from worker
      if (r.progress) setStatus(`${r.status || ""} • ${r.progress}`);
      if (r.status === "done" || r.status === "error") return r;
      await sleep(2000);
    }
  }

  async function runAnalyze() {
    if (!selectedBidId) return;
    setBusy(true);
    setError("");
    setRun(null);
    setStatus("queued");
    try {
      const out = await tmBidAnalyze({ bidId: selectedBidId, mode });
      setStatus(out.status || "queued");
      const finalRun = await pollRun(out.runId);
      setRun(finalRun);
      // refresh history so latest appears at top
      await loadHistory({ reset: true, bidId: selectedBidId });
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  async function loadRun(runId) {
    setBusy(true);
    setError("");
    setRun(null);
    setStatus("loading...");
    try {
      const r = await tmGetRun(runId);
      setRun(r);
      setStatus(r.status || "");
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Layout>
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: 16 }}>
      <h2 style={{ marginBottom: 6 }}>TenderMaker — Bid Analyzer</h2>
      <div style={{ color: "#6b7280", marginBottom: 16 }}>
        Select a bid → run extraction → results are stored as “runs” (you can
        reopen old runs from History).
      </div>

      {error ? (
        <div style={{ color: "crimson", marginBottom: 12 }}>{error}</div>
      ) : null}

      {/* ========================= Controls ========================= */}
      <div style={{ ...box, marginBottom: 14 }}>
        <div style={{ fontWeight: 900, marginBottom: 8 }}>Select Bid</div>

        <div
          style={{
            display: "flex",
            gap: 10,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <select
            value={selectedBidId}
            onChange={(e) => setSelectedBidId(e.target.value)}
            style={{
              padding: 10,
              borderRadius: 10,
              border: "1px solid #e5e7eb",
              minWidth: 420,
            }}
            disabled={busy}
          >
            <option value="">-- Choose a bid --</option>
            {bids.map((b) => (
              <option key={b.id} value={b.id}>
                {b.title} • {b.id}
              </option>
            ))}
          </select>

          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            disabled={busy}
            style={{
              padding: 10,
              borderRadius: 10,
              border: "1px solid #e5e7eb",
            }}
          >
            <option value="auto">auto (text then OCR if needed)</option>
            <option value="text">text only (cheap)</option>
            <option value="ocr">OCR only (DocAI)</option>
          </select>

          <button
            onClick={runAnalyze}
            style={btn}
            disabled={busy || !selectedBidId}
          >
            {busy ? "Analyzing…" : "Run Extraction"}
          </button>

          <button
            onClick={() => refreshBids().catch((e) => setError(String(e)))}
            style={btn}
            disabled={busy}
          >
            Refresh Bids
          </button>
        </div>

        {selectedBid ? (
          <div style={{ marginTop: 10, color: "#6b7280", fontSize: 13 }}>
            Selected:{" "}
            <span style={{ color: "#111827", fontWeight: 800 }}>
              {selectedBid.title}
            </span>{" "}
            • Status: <b>{status || "-"}</b>
          </div>
        ) : (
          <div style={{ marginTop: 10, color: "#6b7280", fontSize: 13 }}>
            Tip: create a bid in <b>/tendermaker/bids</b>, upload PDFs, then
            analyze here.
          </div>
        )}
      </div>

      {/* ========================= History ========================= */}
      <div style={{ ...box, marginBottom: 14 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ fontWeight: 900 }}>History (Saved Analyses)</div>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <select
              value={histStatusFilter}
              onChange={(e) => {
                const v = e.target.value;
                setHistStatusFilter(v);
                setHistCursor(null);
                setHistHasMore(false);
                setHistory([]);
                if (selectedBidId)
                  loadHistory({
                    reset: true,
                    bidId: selectedBidId,
                    statusFilter: v,
                  }).catch(() => {});
              }}
              style={{
                padding: 8,
                borderRadius: 10,
                border: "1px solid #e5e7eb",
              }}
              disabled={!selectedBidId || histBusy}
              title="Filter by status"
            >
              <option value="">all statuses</option>
              <option value="queued">queued</option>
              <option value="running">running</option>
              <option value="done">done</option>
              <option value="error">error</option>
            </select>

            <button
              style={smallBtn}
              disabled={!selectedBidId || histBusy}
              onClick={() =>
                loadHistory({ reset: true, bidId: selectedBidId }).catch(
                  () => {}
                )
              }
            >
              {histBusy ? "Loading…" : "Reload"}
            </button>
          </div>
        </div>

        {!tmListRuns ? (
          <div style={{ marginTop: 10, color: "crimson" }}>
            History API not wired yet. Add <code>tmListRuns()</code> in{" "}
            <code>tendermakerApi.js</code> and backend route{" "}
            <code>GET /runs</code>.
          </div>
        ) : null}

        <div style={{ marginTop: 10 }}>
          {!selectedBidId ? (
            <div style={{ color: "#6b7280" }}>
              Select a bid to see saved analyses.
            </div>
          ) : history.length === 0 ? (
            <div style={{ color: "#6b7280" }}>
              {histBusy ? "Loading history…" : "No saved analyses yet."}
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {history.map((h) => (
                <div
                  key={h.id}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    padding: 12,
                    background: "#fafafa",
                    display: "flex",
                    gap: 12,
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  <div style={{ minWidth: 420 }}>
                    <div style={{ fontWeight: 900, color: "#111827" }}>
                      Run:{" "}
                      <span style={{ fontFamily: "monospace" }}>{h.id}</span>
                    </div>

                    <div
                      style={{ color: "#6b7280", fontSize: 13, marginTop: 4 }}
                    >
                      status: <b>{h.status || "-"}</b> • mode:{" "}
                      <b>{h.mode || "-"}</b> • created:{" "}
                      <b>{fmtTime(h.createdAt)}</b>
                    </div>

                    <div
                      style={{ color: "#6b7280", fontSize: 13, marginTop: 4 }}
                    >
                      pages: <b>{h.pages ?? "-"}</b> • chunks:{" "}
                      <b>{h.chunks ?? "-"}</b> • chars:{" "}
                      <b>{h.textChars ?? "-"}</b>
                    </div>

                    {h.progress ? (
                      <div
                        style={{
                          color: "#6b7280",
                          fontSize: 12,
                          marginTop: 4,
                        }}
                      >
                        progress: <b>{h.progress}</b>{" "}
                        {h.progressAt ? `(${fmtTime(h.progressAt)})` : ""}
                      </div>
                    ) : null}

                    {h.error ? (
                      <div
                        style={{ color: "crimson", fontSize: 13, marginTop: 6 }}
                      >
                        {h.error}
                      </div>
                    ) : null}
                  </div>

                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button
                      style={smallBtn}
                      onClick={() => loadRun(h.id)}
                      disabled={busy}
                    >
                      Open
                    </button>
                  </div>
                </div>
              ))}

              {histHasMore ? (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 6 }}>
                  <button
                    style={btn}
                    disabled={histBusy}
                    onClick={() =>
                      loadHistory({ reset: false, bidId: selectedBidId }).catch(
                        () => {}
                      )
                    }
                  >
                    {histBusy ? "Loading…" : "Load more"}
                  </button>
                </div>
              ) : null}
            </div>
          )}
        </div>
      </div>

      {/* ========================= Result ========================= */}
      {run ? (
        <div style={box}>
          <div style={{ fontWeight: 900, marginBottom: 10 }}>Result</div>

          {run.status === "error" ? (
            <div style={{ color: "crimson" }}>
              {run.error || "Unknown error"}
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "flex",
                  gap: 14,
                  flexWrap: "wrap",
                  marginBottom: 10,
                  color: "#6b7280",
                }}
              >
                <div>
                  pages: <b style={{ color: "#111827" }}>{run.pages || "-"}</b>
                </div>
                <div>
                  chunks: <b style={{ color: "#111827" }}>{run.chunks || "-"}</b>
                </div>
                <div>
                  chars: <b style={{ color: "#111827" }}>{run.textChars || "-"}</b>
                </div>
              </div>

              {/* Raw model output */}
              {run.extracted ? (
                <>
                  <div style={{ fontWeight: 800, marginTop: 10, marginBottom: 6 }}>
                    Raw extracted (model output)
                  </div>
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      fontSize: 13,
                      background: "#fafafa",
                      padding: 12,
                      borderRadius: 10,
                    }}
                  >
                    {JSON.stringify(run.extracted, null, 2)}
                  </pre>
                </>
              ) : null}

              {/* Merged */}
              {run.merged ? (
                <>
                  <div style={{ fontWeight: 800, marginTop: 10, marginBottom: 6 }}>
                    Deduped requirements (merged)
                  </div>
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      fontSize: 13,
                      background: "#fafafa",
                      padding: 12,
                      borderRadius: 10,
                    }}
                  >
                    {JSON.stringify(run.merged, null, 2)}
                  </pre>
                </>
              ) : null}

              {/* DocPack */}
              {run.docPack ? (
                <>
                  <div style={{ fontWeight: 800, marginTop: 10, marginBottom: 6 }}>
                    Document Pack Plan
                  </div>
                  <pre
                    style={{
                      whiteSpace: "pre-wrap",
                      fontSize: 13,
                      background: "#fafafa",
                      padding: 12,
                      borderRadius: 10,
                    }}
                  >
                    {JSON.stringify(run.docPack, null, 2)}
                  </pre>
                </>
              ) : null}

              <ReviewWidget
                docId={null}
                runId={run.id}
                extracted={run.extracted || null}
              />
            </>
          )}
        </div>
      ) : null}
    </div>
    </Layout>
  );
}
